import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import styles from "./style.module.scss"
import { Bar } from 'react-chartjs-2';
import { t } from 'i18next';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Helper function to truncate text with ellipsis
const truncateText = (text, maxLength = 10) => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Helper function to get sentiment color based on CJ score
const getSentimentColor = (score) => {
  if (score >= 20) {
    return 'rgba(34, 197, 94, 0.8)'; // Green for good scores (60+)
  } else if (score >= 0) {
    return 'rgba(234, 179, 8, 0.8)'; // Yellow for moderate scores (40-59)
  } else {
    return 'rgba(239, 68, 68, 0.8)'; // Red for poor scores (<40)
  }
};

// Helper function to get sentiment border color
const getSentimentBorderColor = (score) => {
  if (score >= 20) {
    return 'rgba(34, 197, 94, 1)'; // Green border
  } else if (score >= 0) {
    return 'rgba(234, 179, 8, 1)'; // Yellow border
  } else {
    return 'rgba(239, 68, 68, 1)'; // Red border
  }
};

// Helper function to create multi-level axis labels with proper grouping
const createMultiLevelLabels = (data) => {
  const bottomLabels = []; // Individual items (brands/metrics)
  const truncatedLabels = []; // Truncated versions for display
  const values = [];
  const parentContext = [];
  const groupInfo = []; // Track grouping information
  
  // Handle empty or undefined data
  if (!data || Object.keys(data).length === 0) {
    return { bottomLabels, truncatedLabels, values, parentContext, groupInfo };
  }
  
  Object.keys(data).forEach(industryKey => {
    Object.keys(data[industryKey]).forEach(categoryKey => {
      const categoryItems = [];
      Object.keys(data[industryKey][categoryKey]).forEach(brandKey => {
        const item = data[industryKey][categoryKey][brandKey];
        if (item.cj_score !== undefined) {
          bottomLabels.push(brandKey);
          truncatedLabels.push(truncateText(brandKey));
          values.push(item.cj_score);
          parentContext.push({ 
            industry: industryKey, 
            category: categoryKey, 
            brand: brandKey,
            positive_reviews: item.positive_reviews || 0,
            negative_reviews: item.negative_reviews || 0,
            total_reviews: item.total_reviews || 0,
            step_topics: item.step_topics || {},
            step_id: item.step_id || null // Include step_id if available
          });
          categoryItems.push(bottomLabels.length - 1); // Track index
        }
      });
      
      if (categoryItems.length > 0) {
        groupInfo.push({
          type: 'category',
          label: categoryKey,
          startIndex: categoryItems[0],
          endIndex: categoryItems[categoryItems.length - 1],
          parent: industryKey
        });
      }
    });
    
    // Group by industry
    const industryItems = groupInfo.filter(g => g.parent === industryKey);
    if (industryItems.length > 0) {
      groupInfo.push({
        type: 'industry',
        label: industryKey,
        startIndex: industryItems[0].startIndex,
        endIndex: industryItems[industryItems.length - 1].endIndex,
        parent: null
      });
    }
  });
  
  return { bottomLabels, truncatedLabels, values, parentContext, groupInfo };
};

// Custom plugin to draw multi-level labels
const multiLevelLabelsPlugin = {
  id: 'multiLevelLabels',
  afterDraw: (chart, args, options) => {
    const { ctx } = chart;
    const { groupInfo, theme = 'light' } = options;
    
    if (!groupInfo) return;
    
    const xAxis = chart.scales.x;
    const chartArea = chart.chartArea;
    
    // Set text properties
    const textColor = theme === 'dark' ? '#ffffff' : '#333333';
    ctx.fillStyle = textColor;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    
    // Draw category labels
    const categoryGroups = groupInfo.filter(g => g.type === 'category');
    ctx.font = 'bold 12px Arial';
    
    categoryGroups.forEach(group => {
      const startPixel = xAxis.getPixelForValue(group.startIndex);
      const endPixel = xAxis.getPixelForValue(group.endIndex);
      const centerX = (startPixel + endPixel) / 2;
      
      // Draw category label
      ctx.fillText(group.label, centerX, chartArea.bottom + 100);
      
      // Draw category bracket
      ctx.strokeStyle = textColor;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(startPixel - 10, chartArea.bottom + 95);
      ctx.lineTo(startPixel - 10, chartArea.bottom + 98);
      ctx.lineTo(endPixel + 10, chartArea.bottom + 98);
      ctx.lineTo(endPixel + 10, chartArea.bottom + 95);
      ctx.stroke();
    });
    
    // Draw industry labels
    const industryGroups = groupInfo.filter(g => g.type === 'industry');
    ctx.font = 'bold 14px Arial';
    
    industryGroups.forEach(group => {
      const startPixel = xAxis.getPixelForValue(group.startIndex);
      const endPixel = xAxis.getPixelForValue(group.endIndex);
      const centerX = (startPixel + endPixel) / 2;
      
      // Draw industry label
      ctx.fillText(group.label, centerX, chartArea.bottom + 140);
      
      // Draw industry bracket
      ctx.strokeStyle = textColor;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(startPixel - 15, chartArea.bottom + 135);
      ctx.lineTo(startPixel - 15, chartArea.bottom + 138);
      ctx.lineTo(endPixel + 15, chartArea.bottom + 138);
      ctx.lineTo(endPixel + 15, chartArea.bottom + 135);
      ctx.stroke();
    });
  }
};

// Custom plugin to add rounded corners to bars (now using built-in borderRadius)
const roundedCornersPlugin = {
  id: 'roundedCorners',
  // Plugin is now simplified since we're using built-in borderRadius
  // This can be removed if the built-in borderRadius works perfectly
};

// Register the custom plugins
ChartJS.register(multiLevelLabelsPlugin, roundedCornersPlugin);

const BarChart = ({ ChartData, theme = 'dark', onBarClick }) => {
  // Handle case when ChartData is undefined or empty
  if (!ChartData || Object.keys(ChartData).length === 0) {
    return (
      <div className={styles.customJourneyChartEmpty}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100%',
          color: theme === 'dark' ? '#ffffff' : '#333333',
          fontSize: '16px'
        }}>
          {t("common.no_data")}
        </div>
      </div>
    );
  }

  const { bottomLabels, truncatedLabels, values, parentContext, groupInfo } = createMultiLevelLabels(ChartData);
  
  // Generate sentiment-based colors for each bar
  const backgroundColor = values.map(score => getSentimentColor(score));
  const borderColor = values.map(score => getSentimentBorderColor(score));
  
  const data = {
    labels: truncatedLabels, // Use truncated labels for display
    datasets: [
      {
        label: "",
        data: values,
        backgroundColor: backgroundColor,
        borderColor: borderColor,
        borderWidth: 0,
        barPercentage: 0.5,
        categoryPercentage: 0.7,
        barThickness: 18,
        borderSkipped: false,
        borderRadius: {
          topLeft: 50,
          topRight: 50,
          bottomLeft: 50,
          bottomRight: 50,
        },
        // Hide default bars to let custom plugin draw rounded ones
        pointRadius: 0,
        pointHoverRadius: 0,
      },
    ],
  };

  const options = {
    responsive: false, // Disable responsive to allow fixed width
    maintainAspectRatio: false,
    onClick: (event, elements) => {
      if (elements.length > 0 && onBarClick) {
        const element = elements[0];
        const index = element.index;
        const context = parentContext[index];
        if (context) {
          onBarClick({
            brand: context.brand,
            industry: context.industry,
            category: context.category,
            cjScore: values[index],
            positiveReviews: context.positive_reviews,
            negativeReviews: context.negative_reviews,
            totalReviews: context.total_reviews,
            stepTopics: context.step_topics,
            step_id: context.step_id // Pass step_id from chart data
          });
        }
      }
    },
    layout: {
      padding: {
        left: 20,
        right: 20,
        top: 20,
        bottom: 150 // Extra space for rotated labels and multi-level labels
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
        },
        ticks: {
          color: theme === 'dark' ? '#ffffff' : '#333333',
          display: true, // Show y-axis numbers
        },
        title: {
          display: false,
        }
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: theme === 'dark' ? '#ffffff' : '#333333',
          maxRotation: 45,
          minRotation: 45,
          font: {
            size: 9
          },
          autoSkip: false, // Show all labels when horizontal scrolling
          maxTicksLimit: undefined, // Remove limit to show all labels
          callback: function(value, index, ticks) {
            // Truncate long labels
            const label = this.getLabelForValue(value);
            return label.length > 10 ? label.substring(0, 10) + '...' : label;
          }
        }
      }
    },
    plugins: {
      datalabels: {
        color: (ctx) => (ctx.dataset.data[ctx.dataIndex] < 0 ? "#fff" : "#111"),
        font: { weight: "500", size: 11 },
        anchor: "center",
        align: "center",
        display: true,
      },
      legend: {
        display: false,
      }, 
      title: {
        display: false,

      },
      tooltip: {
        backgroundColor: theme === 'dark' ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.98)',
        titleColor: theme === 'dark' ? '#ffffff' : '#333333',
        bodyColor: theme === 'dark' ? '#ffffff' : '#333333',
        borderColor: theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 12
        },
        titleSpacing: 4,
        bodySpacing: 2,
        callbacks: {
          title: function(tooltipItems) {
            const index = tooltipItems[0].dataIndex;
            const context = parentContext[index];
            return `${context.brand}`; // Show full brand name in tooltip
          },
          label: function(context) {
            const index = context.dataIndex;
            const contextInfo = parentContext[index];
            const score = context.parsed.y;
            
            // Build the tooltip content according to the specified structure
            const tooltipContent = [
              `${t("customer_journey.13")}: ${score}`,
              `${t("customer_journey.14")}: ${contextInfo.total_reviews}`,
              `  ${t("customer_journey.16")}: ${contextInfo.positive_reviews}`,
              `  ${t("customer_journey.17")}: ${contextInfo.negative_reviews}`,
              ` `, // Empty line for spacing
              `${t("customer_journey.15")}:`
            ];
            
            // Add step topics with positive and negative numbers
            if (contextInfo.step_topics && Object.keys(contextInfo.step_topics).length > 0) {
              Object.entries(contextInfo.step_topics).forEach(([topicName, topicData]) => {
                tooltipContent.push(`  • ${topicName}: ${topicData.positive || 0} ${t("customer_journey.16")} - ${topicData.negative || 0} ${t("customer_journey.17")}`);
              });
            } else {
              // Show message when no step topics are available
              tooltipContent.push(`  ${t("common.no_data")}`);
            }
            
            return tooltipContent;
          }
        }
      },
      multiLevelLabels: {
        groupInfo: groupInfo,
        theme: theme
      },
      roundedCorners: roundedCornersPlugin // Enable rounded corners plugin
    },
  };

  return (
    <div className={styles.customJourneyChart}>
      <Bar data={data} options={options} width={1200} height={520} />
    </div>
  );
};

// Demo component to showcase the chart
const ChartDemo = ({ theme = 'light' }) => {
  const [currentTheme, setCurrentTheme] = React.useState(theme);

  return (
    <div style={{ 
      padding: '20px', 
      minHeight: '100vh',
      backgroundColor: currentTheme === 'dark' ? '#0d1117' : '#f6f8fa',
      color: currentTheme === 'dark' ? '#ffffff' : '#333333',
      transition: 'all 0.3s ease'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '40px'
      }}>
        <h1 style={{ textAlign: 'center', color: 'currentColor', margin: 0 }}>
          Customer Journey Sentiment Analysis
        </h1>
        
        <button 
          onClick={() => setCurrentTheme(currentTheme === 'light' ? 'dark' : 'light')}
          style={{
            padding: '10px 20px',
            backgroundColor: currentTheme === 'dark' ? '#21262d' : '#ffffff',
            color: currentTheme === 'dark' ? '#ffffff' : '#333333',
            border: `2px solid ${currentTheme === 'dark' ? '#30363d' : '#d0d7de'}`,
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = currentTheme === 'dark' ? '#30363d' : '#f6f8fa';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = currentTheme === 'dark' ? '#21262d' : '#ffffff';
          }}
        >
          {currentTheme === 'light' ? '🌙 Dark Mode' : '☀️ Light Mode'}
        </button>
      </div>
      
      {/* Legend */}
      <div style={{ 
        marginBottom: '20px', 
        display: 'flex', 
        justifyContent: 'center', 
        gap: '30px',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ 
            width: '20px', 
            height: '15px', 
            backgroundColor: 'rgba(34, 197, 94, 0.8)', 
            borderRadius: '3px',
            border: '1px solid rgba(34, 197, 94, 1)'
          }}></div>
          <span style={{ fontSize: '14px', fontWeight: '500' }}>Good (60+)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ 
            width: '20px', 
            height: '15px', 
            backgroundColor: 'rgba(234, 179, 8, 0.8)', 
            borderRadius: '3px',
            border: '1px solid rgba(234, 179, 8, 1)'
          }}></div>
          <span style={{ fontSize: '14px', fontWeight: '500' }}>Moderate (40-59)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ 
            width: '20px', 
            height: '15px', 
            backgroundColor: 'rgba(239, 68, 68, 0.8)', 
            borderRadius: '3px',
            border: '1px solid rgba(239, 68, 68, 1)'
          }}></div>
          <span style={{ fontSize: '14px', fontWeight: '500' }}>Poor (&lt;40)</span>
        </div>
      </div>
      
      <div style={{ marginBottom: '40px' }}>
        <BarChart theme={currentTheme} />
      </div>
      </div>
  );
};

export default ChartDemo;
export { BarChart , ChartDemo };